/**
 * Presence networking: identifies the user, joins rooms, relays room-board and membership
 * updates, carries the 20 Hz pose broadcast and WebRTC signalling.
 *
 * Study rooms are joined by key (`ONC-2041.K7P`: the study and the room code). The room's
 * shared view (`view`) and the host's lines (`host:say`) arrive here for DashboardRoom.
 */
import {
  LOBBY_ROOM,
  type Annotation,
  type DashboardId,
  type MemberInfo,
  type PinnedPanel,
  type PoseData,
  type Query,
  type RoomSummary,
  type ModelPin,
  type PresenterState,
  type Reaction,
  type SharedModel,
  type Slide,
  type WbNote,
  type WbStroke,
  type WhiteboardState,
  type ModelKind,
  type HostSayKind,
  type ViewEntry,
  parseRoomKey,
} from '@clinspace/shared';
import { SocketClient } from '../lib/ws.ts';
import { isValidName, type Profile } from './profile.ts';

type Listener<T> = (value: T) => void;

/** A line the room's host says, as it comes back from the server. */
export interface HostLine {
  id: string;
  text: string;
  kind: HostSayKind;
  to?: string;
  question?: string;
  from: { sessionId: string; name: string };
}

export class Net {
  readonly socket = new SocketClient();
  rooms: RoomSummary[] = [];
  members: MemberInfo[] = [];
  currentRoom: string | undefined;
  private identified = false;
  private profile: Profile | undefined;
  private muted = false;
  private roomListeners = new Set<Listener<RoomSummary[]>>();
  private memberListeners = new Set<Listener<MemberInfo[]>>();
  private snapshotListeners = new Set<
    (members: MemberInfo[], poses: Record<string, PoseData>) => void
  >();
  private joinedListeners = new Set<Listener<MemberInfo>>();
  private leftListeners = new Set<Listener<string>>();
  private poseListeners = new Set<(id: string, pose: PoseData) => void>();
  private signalListeners = new Set<(from: string, data: unknown) => void>();
  private errorListeners = new Set<(code: string, message: string) => void>();
  private memberUpdateListeners = new Set<
    (id: string, patch: { muted?: boolean; height?: number; handRaised?: boolean }) => void
  >();
  /** Dashboard clones on the table of the current room. */
  pins: PinnedPanel[] = [];
  private pinsListeners = new Set<Listener<PinnedPanel[]>>();
  private pinAddedListeners = new Set<Listener<PinnedPanel>>();
  private pinRemovedListeners = new Set<Listener<string>>();
  private broadcastListeners = new Set<
    (from: { sessionId: string; name: string }, dashboard: DashboardId, path: string[]) => void
  >();
  /** The current room's shared view: the latest entry per key. */
  view = new Map<string, ViewEntry>();
  /** The room whose snapshot arrived last (the shared view is valid only for it). */
  snapshotRoom: string | undefined;
  private viewListeners = new Set<
    (entries: ViewEntry[], from: { sessionId: string; name: string }) => void
  >();
  private hostSayListeners = new Set<(line: HostLine) => void>();
  private hostStopListeners = new Set<(from: { sessionId: string; name: string }) => void>();
  private queryListeners = new Set<(query: Query, event: 'created' | 'updated') => void>();
  /** Shared notes in the current room. */
  annotations: Annotation[] = [];
  private annotationsListeners = new Set<Listener<Annotation[]>>();
  private beamListeners = new Set<
    (
      from: { sessionId: string; name: string },
      origin: [number, number, number],
      target: [number, number, number] | null,
    ) => void
  >();

  /* ---------- Collaboration tools (Phase 6) ---------- */
  whiteboard: WhiteboardState = { strokes: [], notes: [] };
  models: SharedModel[] = [];
  presenter: PresenterState | null = null;
  sharer: { sessionId: string; name: string } | null = null;
  private whiteboardListeners = new Set<Listener<WhiteboardState>>();
  private strokeListeners = new Set<Listener<WbStroke>>();
  private noteListeners = new Set<Listener<WbNote>>();
  private eraseListeners = new Set<Listener<string[]>>();
  private modelsListeners = new Set<Listener<SharedModel[]>>();
  private modelPlacedListeners = new Set<Listener<SharedModel>>();
  private modelUpdatedListeners = new Set<
    (id: string, patch: Partial<SharedModel>, from: string) => void
  >();
  private modelPinListeners = new Set<(id: string, pins: ModelPin[]) => void>();
  private modelRemovedListeners = new Set<Listener<string>>();
  private presenterListeners = new Set<Listener<PresenterState | null>>();
  private recentreListeners = new Set<Listener<{ sessionId: string; name: string }>>();
  private shareListeners = new Set<(sessionId: string, name: string, on: boolean) => void>();
  private reactionListeners = new Set<
    (from: { sessionId: string; name: string }, reaction: Reaction) => void
  >();

  constructor() {
    this.socket.on('wb:stroke', (m) => {
      if (m.room !== this.currentRoom) return;
      const i = this.whiteboard.strokes.findIndex((x) => x.id === m.stroke.id);
      if (i >= 0) this.whiteboard.strokes[i] = m.stroke;
      else this.whiteboard.strokes.push(m.stroke);
      for (const l of this.strokeListeners) l(m.stroke);
    });
    this.socket.on('wb:note', (m) => {
      if (m.room !== this.currentRoom) return;
      const i = this.whiteboard.notes.findIndex((x) => x.id === m.note.id);
      if (i >= 0) this.whiteboard.notes[i] = m.note;
      else this.whiteboard.notes.push(m.note);
      for (const l of this.noteListeners) l(m.note);
    });
    this.socket.on('wb:erase', (m) => {
      if (m.room !== this.currentRoom) return;
      const ids = new Set(m.ids);
      this.whiteboard.strokes = this.whiteboard.strokes.filter((x) => !ids.has(x.id));
      this.whiteboard.notes = this.whiteboard.notes.filter((x) => !ids.has(x.id));
      for (const l of this.eraseListeners) l(m.ids);
    });
    this.socket.on('wb:clear', (m) => {
      if (m.room !== this.currentRoom) return;
      this.whiteboard = { strokes: [], notes: [] };
      for (const l of this.whiteboardListeners) l(this.whiteboard);
    });
    this.socket.on('model:placed', (m) => {
      if (m.room !== this.currentRoom) return;
      this.models = [...this.models.filter((x) => x.id !== m.model.id), m.model];
      for (const l of this.modelPlacedListeners) l(m.model);
    });
    this.socket.on('model:updated', (m) => {
      if (m.room !== this.currentRoom) return;
      const model = this.models.find((x) => x.id === m.id);
      if (model) Object.assign(model, m.patch);
      for (const l of this.modelUpdatedListeners) l(m.id, m.patch, m.from);
    });
    this.socket.on('model:pinned', (m) => {
      if (m.room !== this.currentRoom) return;
      const model = this.models.find((x) => x.id === m.id);
      if (!model) return;
      model.pins = [...model.pins.filter((x) => x.id !== m.pin.id), m.pin];
      for (const l of this.modelPinListeners) l(m.id, model.pins);
    });
    this.socket.on('model:unpinned', (m) => {
      if (m.room !== this.currentRoom) return;
      const model = this.models.find((x) => x.id === m.id);
      if (!model) return;
      model.pins = model.pins.filter((x) => x.id !== m.pinId);
      for (const l of this.modelPinListeners) l(m.id, model.pins);
    });
    this.socket.on('model:removed', (m) => {
      if (m.room !== this.currentRoom) return;
      this.models = this.models.filter((x) => x.id !== m.id);
      for (const l of this.modelRemovedListeners) l(m.id);
    });
    this.socket.on('present:state', (m) => {
      if (m.room !== this.currentRoom) return;
      this.presenter = m.presenter;
      for (const l of this.presenterListeners) l(m.presenter);
    });
    this.socket.on('present:recentre', (m) => {
      if (m.room !== this.currentRoom) return;
      for (const l of this.recentreListeners) l(m.from);
    });
    this.socket.on('share:state', (m) => {
      if (m.room !== this.currentRoom) return;
      this.sharer = m.on ? { sessionId: m.sessionId, name: m.name } : null;
      for (const l of this.shareListeners) l(m.sessionId, m.name, m.on);
    });
    this.socket.on('reaction', (m) => {
      if (m.room !== this.currentRoom) return;
      for (const l of this.reactionListeners) l(m.from, m.reaction);
    });
    this.socket.on('rooms:update', (m) => {
      this.rooms = m.rooms;
      for (const l of this.roomListeners) l(this.rooms);
    });
    this.socket.on('room:snapshot', (m) => {
      this.currentRoom = m.room;
      this.members = m.members;
      this.pins = m.pins ?? [];
      this.annotations = m.annotations ?? [];
      this.whiteboard = m.whiteboard ?? { strokes: [], notes: [] };
      this.models = m.models ?? [];
      this.presenter = m.presenter ?? null;
      this.sharer = m.sharer ?? null;
      this.view = new Map((m.view ?? []).map((e) => [e.key, e]));
      this.snapshotRoom = m.room;
      for (const l of this.memberListeners) l(this.members);
      for (const l of this.snapshotListeners) l(this.members, m.poses ?? {});
      for (const l of this.pinsListeners) l(this.pins);
      for (const l of this.annotationsListeners) l(this.annotations);
      for (const l of this.whiteboardListeners) l(this.whiteboard);
      for (const l of this.modelsListeners) l(this.models);
      for (const l of this.presenterListeners) l(this.presenter);
      if (this.sharer)
        for (const l of this.shareListeners) l(this.sharer.sessionId, this.sharer.name, true);
    });
    this.socket.on('annotation:added', (m) => {
      if (m.room !== this.currentRoom) return;
      this.annotations = [
        ...this.annotations.filter((a) => a.id !== m.annotation.id),
        m.annotation,
      ];
      for (const l of this.annotationsListeners) l(this.annotations);
    });
    this.socket.on('annotation:removed', (m) => {
      if (m.room !== this.currentRoom) return;
      this.annotations = this.annotations.filter((a) => a.id !== m.id);
      for (const l of this.annotationsListeners) l(this.annotations);
    });
    this.socket.on('beam', (m) => {
      if (m.room !== this.currentRoom) return;
      for (const l of this.beamListeners) l(m.from, m.origin, m.target);
    });
    this.socket.on('pin:added', (m) => {
      if (m.room !== this.currentRoom) return;
      this.pins = [...this.pins.filter((p) => p.id !== m.pin.id), m.pin];
      for (const l of this.pinAddedListeners) l(m.pin);
    });
    this.socket.on('pin:removed', (m) => {
      if (m.room !== this.currentRoom) return;
      this.pins = this.pins.filter((p) => p.id !== m.id);
      for (const l of this.pinRemovedListeners) l(m.id);
    });
    this.socket.on('broadcast:view', (m) => {
      if (m.room !== this.currentRoom) return;
      for (const l of this.broadcastListeners) l(m.from, m.dashboard, m.path);
    });
    this.socket.on('view:set', (m) => {
      if (m.room !== this.currentRoom) return;
      for (const e of m.entries) {
        if (e.value === null) this.view.delete(e.key);
        else this.view.set(e.key, e);
      }
      for (const l of this.viewListeners) l(m.entries, m.from);
    });
    this.socket.on('host:say', (m) => {
      if (m.room !== this.currentRoom) return;
      const { type: _type, room: _room, ...line } = m;
      for (const l of this.hostSayListeners) l(line);
    });
    this.socket.on('host:stop', (m) => {
      if (m.room !== this.currentRoom) return;
      for (const l of this.hostStopListeners) l(m.from);
    });
    this.socket.on('query:created', (m) => {
      for (const l of this.queryListeners) l(m.query, 'created');
    });
    this.socket.on('query:updated', (m) => {
      for (const l of this.queryListeners) l(m.query, 'updated');
    });
    this.socket.on('member:joined', (m) => {
      if (m.room !== this.currentRoom) return;
      this.members = [...this.members.filter((x) => x.sessionId !== m.member.sessionId), m.member];
      for (const l of this.memberListeners) l(this.members);
      for (const l of this.joinedListeners) l(m.member);
    });
    this.socket.on('member:left', (m) => {
      if (m.room !== this.currentRoom) return;
      this.members = this.members.filter((x) => x.sessionId !== m.sessionId);
      for (const l of this.memberListeners) l(this.members);
      for (const l of this.leftListeners) l(m.sessionId);
    });
    this.socket.on('member:update', (m) => {
      if (m.room !== this.currentRoom) return;
      const member = this.members.find((x) => x.sessionId === m.sessionId);
      if (member) {
        if (m.muted !== undefined) member.muted = m.muted;
        if (m.height !== undefined) member.height = m.height;
        if (m.handRaised !== undefined) member.handRaised = m.handRaised;
      }
      for (const l of this.memberUpdateListeners)
        l(m.sessionId, { muted: m.muted, height: m.height, handRaised: m.handRaised });
    });
    this.socket.on('pose', (m) => {
      const { type: _type, id, ...pose } = m;
      for (const l of this.poseListeners) l(id, pose);
    });
    this.socket.on('rtc:signal', (m) => {
      for (const l of this.signalListeners) l(m.from, m.data);
    });
    this.socket.on('welcome', () => {
      if (this.identified) this.resend();
    });
    this.socket.on('error', (m) => {
      console.warn(`[net] server error ${m.code}: ${m.message}`);
      for (const l of this.errorListeners) l(m.code, m.message);
    });
  }

  connect(): void {
    this.socket.connect();
  }

  get sessionId(): string | undefined {
    return this.socket.sessionId;
  }

  identify(profile: Profile): void {
    if (!isValidName(profile.name)) {
      console.warn('[net] refusing to identify with an invalid name');
      return;
    }
    this.profile = profile;
    this.identified = true;
    this.socket.send({
      type: 'hello',
      name: profile.name.trim(),
      role: profile.role,
      avatar: profile.vrmUrl ? `vrm:${profile.vrmUrl}` : profile.avatar,
    });
    if (this.muted) this.socket.send({ type: 'member:update', muted: true });
  }

  private resend(): void {
    if (this.profile) this.identify(this.profile);
    if (this.currentRoom) this.join(this.currentRoom);
  }

  /** Joins the lobby or a study room by key (`ONC-2041.K7P`). */
  join(room: string): void {
    this.currentRoom = room;
    // Until the snapshot arrives, nothing of the previous room's view applies.
    this.view = new Map();
    this.snapshotRoom = undefined;
    this.socket.send({ type: 'join', room });
  }

  /** The room code of the study room this session is in. */
  get roomCode(): string | undefined {
    return this.currentRoom ? parseRoomKey(this.currentRoom)?.code : undefined;
  }

  joinLobby(): void {
    this.join(LOBBY_ROOM);
  }

  leave(): void {
    this.currentRoom = undefined;
    this.members = [];
    this.pins = [];
    this.annotations = [];
    this.whiteboard = { strokes: [], notes: [] };
    this.models = [];
    this.presenter = null;
    this.sharer = null;
    this.view = new Map();
    this.snapshotRoom = undefined;
    this.socket.send({ type: 'leave' });
  }

  sendPose(pose: PoseData): void {
    if (!this.currentRoom || !this.socket.connected) return;
    this.socket.send({ type: 'pose', ...pose });
  }

  sendSignal(to: string, data: unknown): void {
    this.socket.send({ type: 'rtc:signal', to, data });
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.socket.send({ type: 'member:update', muted });
  }

  setHeight(height: number): void {
    this.socket.send({ type: 'member:update', height });
  }

  setHandRaised(handRaised: boolean): void {
    this.socket.send({ type: 'member:update', handRaised });
  }

  /* ---------- Collaboration tools (Phase 6) ---------- */

  wbStroke(stroke: Pick<WbStroke, 'id' | 'colour' | 'width' | 'points' | 'done'>): void {
    if (!this.currentRoom || !this.socket.connected) return;
    this.socket.send({ type: 'wb:stroke', ...stroke });
  }

  wbNote(note: { id?: string; u: number; v: number; text: string; colour: string }): void {
    this.socket.send({ type: 'wb:note', ...note });
  }

  wbErase(ids: string[]): void {
    if (ids.length) this.socket.send({ type: 'wb:erase', ids });
  }

  wbUndo(): void {
    this.socket.send({ type: 'wb:undo' });
  }

  wbClear(): void {
    this.socket.send({ type: 'wb:clear' });
  }

  modelPlace(
    kind: ModelKind,
    position: { x: number; y: number; z: number },
    quaternion: [number, number, number, number],
    scale: number,
  ): void {
    this.socket.send({ type: 'model:place', kind, position, quaternion, scale });
  }

  modelUpdate(
    id: string,
    patch: Partial<Pick<SharedModel, 'position' | 'quaternion' | 'scale' | 'exploded'>>,
  ): void {
    if (!this.currentRoom || !this.socket.connected) return;
    this.socket.send({ type: 'model:update', id, ...patch });
  }

  modelGrab(id: string, on: boolean): void {
    this.socket.send({ type: 'model:grab', id, on });
  }

  modelPin(id: string, part: string): void {
    this.socket.send({ type: 'model:pin', id, part });
  }

  modelUnpin(id: string, pinId: string): void {
    this.socket.send({ type: 'model:unpin', id, pinId });
  }

  modelRemove(id: string): void {
    this.socket.send({ type: 'model:remove', id });
  }

  presentStart(): void {
    this.socket.send({ type: 'present:start' });
  }

  presentEnd(): void {
    this.socket.send({ type: 'present:end' });
  }

  presentSlide(slide: Slide | null): void {
    this.socket.send({ type: 'present:slide', slide });
  }

  presentSpotlight(on: boolean): void {
    this.socket.send({ type: 'present:spotlight', on });
  }

  presentRecentre(): void {
    this.socket.send({ type: 'present:recentre' });
  }

  shareState(on: boolean): void {
    this.socket.send({ type: 'share:state', on });
  }

  sendReaction(reaction: Reaction): void {
    if (!this.currentRoom || !this.socket.connected) return;
    this.socket.send({ type: 'reaction', reaction });
  }

  onWhiteboard(listener: Listener<WhiteboardState>): () => void {
    this.whiteboardListeners.add(listener);
    return () => this.whiteboardListeners.delete(listener);
  }

  onStroke(listener: Listener<WbStroke>): () => void {
    this.strokeListeners.add(listener);
    return () => this.strokeListeners.delete(listener);
  }

  onNote(listener: Listener<WbNote>): () => void {
    this.noteListeners.add(listener);
    return () => this.noteListeners.delete(listener);
  }

  onErase(listener: Listener<string[]>): () => void {
    this.eraseListeners.add(listener);
    return () => this.eraseListeners.delete(listener);
  }

  onModels(listener: Listener<SharedModel[]>): () => void {
    this.modelsListeners.add(listener);
    return () => this.modelsListeners.delete(listener);
  }

  onModelPlaced(listener: Listener<SharedModel>): () => void {
    this.modelPlacedListeners.add(listener);
    return () => this.modelPlacedListeners.delete(listener);
  }

  onModelUpdated(
    listener: (id: string, patch: Partial<SharedModel>, from: string) => void,
  ): () => void {
    this.modelUpdatedListeners.add(listener);
    return () => this.modelUpdatedListeners.delete(listener);
  }

  onModelPins(listener: (id: string, pins: ModelPin[]) => void): () => void {
    this.modelPinListeners.add(listener);
    return () => this.modelPinListeners.delete(listener);
  }

  onModelRemoved(listener: Listener<string>): () => void {
    this.modelRemovedListeners.add(listener);
    return () => this.modelRemovedListeners.delete(listener);
  }

  onPresenter(listener: Listener<PresenterState | null>): () => void {
    this.presenterListeners.add(listener);
    return () => this.presenterListeners.delete(listener);
  }

  onRecentre(listener: Listener<{ sessionId: string; name: string }>): () => void {
    this.recentreListeners.add(listener);
    return () => this.recentreListeners.delete(listener);
  }

  onShare(listener: (sessionId: string, name: string, on: boolean) => void): () => void {
    this.shareListeners.add(listener);
    return () => this.shareListeners.delete(listener);
  }

  onReaction(
    listener: (from: { sessionId: string; name: string }, reaction: Reaction) => void,
  ): () => void {
    this.reactionListeners.add(listener);
    return () => this.reactionListeners.delete(listener);
  }

  /* ---------- Dashboards ---------- */

  pinAdd(dashboard: DashboardId, state: PinnedPanel['state']): void {
    this.socket.send({ type: 'pin:add', dashboard, state });
  }

  pinRemove(id: string): void {
    this.socket.send({ type: 'pin:remove', id });
  }

  broadcastView(dashboard: DashboardId, path: string[]): void {
    this.socket.send({ type: 'broadcast:view', dashboard, path });
  }

  /** Sends changes to the room's shared view (null removes a key). */
  setView(entries: { key: string; value: unknown }[]): void {
    if (!this.currentRoom || !this.socket.connected || !entries.length) return;
    this.socket.send({ type: 'view:set', entries });
  }

  onView(
    listener: (entries: ViewEntry[], from: { sessionId: string; name: string }) => void,
  ): () => void {
    this.viewListeners.add(listener);
    return () => this.viewListeners.delete(listener);
  }

  /** Asks the room's host to say a line; it comes back (to everyone) through onHostSay. */
  hostSay(line: Omit<HostLine, 'from'>): boolean {
    if (!this.currentRoom || !this.socket.connected) return false;
    this.socket.send({ type: 'host:say', ...line });
    return true;
  }

  hostStop(): void {
    if (this.currentRoom && this.socket.connected) this.socket.send({ type: 'host:stop' });
  }

  onHostSay(listener: (line: HostLine) => void): () => void {
    this.hostSayListeners.add(listener);
    return () => this.hostSayListeners.delete(listener);
  }

  onHostStop(listener: (from: { sessionId: string; name: string }) => void): () => void {
    this.hostStopListeners.add(listener);
    return () => this.hostStopListeners.delete(listener);
  }

  addAnnotation(
    position: { x: number; y: number; z: number },
    text: string,
    dashboard?: DashboardId,
  ): void {
    this.socket.send({
      type: 'annotation:add',
      position,
      text,
      ...(dashboard ? { dashboard } : {}),
    });
  }

  removeAnnotation(id: string): void {
    this.socket.send({ type: 'annotation:remove', id });
  }

  sendBeam(origin: [number, number, number], target: [number, number, number] | null): void {
    if (!this.currentRoom || !this.socket.connected) return;
    this.socket.send({ type: 'beam', origin, target });
  }

  onAnnotations(listener: Listener<Annotation[]>): () => void {
    this.annotationsListeners.add(listener);
    return () => this.annotationsListeners.delete(listener);
  }

  onBeam(
    listener: (
      from: { sessionId: string; name: string },
      origin: [number, number, number],
      target: [number, number, number] | null,
    ) => void,
  ): () => void {
    this.beamListeners.add(listener);
    return () => this.beamListeners.delete(listener);
  }

  onPins(listener: Listener<PinnedPanel[]>): () => void {
    this.pinsListeners.add(listener);
    return () => this.pinsListeners.delete(listener);
  }

  onPinAdded(listener: Listener<PinnedPanel>): () => void {
    this.pinAddedListeners.add(listener);
    return () => this.pinAddedListeners.delete(listener);
  }

  onPinRemoved(listener: Listener<string>): () => void {
    this.pinRemovedListeners.add(listener);
    return () => this.pinRemovedListeners.delete(listener);
  }

  onBroadcast(
    listener: (
      from: { sessionId: string; name: string },
      dashboard: DashboardId,
      path: string[],
    ) => void,
  ): () => void {
    this.broadcastListeners.add(listener);
    return () => this.broadcastListeners.delete(listener);
  }

  onQuery(listener: (query: Query, event: 'created' | 'updated') => void): () => void {
    this.queryListeners.add(listener);
    return () => this.queryListeners.delete(listener);
  }

  /** Members other than this session. */
  others(): MemberInfo[] {
    return this.members.filter((m) => m.sessionId !== this.sessionId);
  }

  onRooms(listener: Listener<RoomSummary[]>): () => void {
    this.roomListeners.add(listener);
    if (this.rooms.length) listener(this.rooms);
    return () => this.roomListeners.delete(listener);
  }

  onMembers(listener: Listener<MemberInfo[]>): () => void {
    this.memberListeners.add(listener);
    return () => this.memberListeners.delete(listener);
  }

  onSnapshot(
    listener: (members: MemberInfo[], poses: Record<string, PoseData>) => void,
  ): () => void {
    this.snapshotListeners.add(listener);
    return () => this.snapshotListeners.delete(listener);
  }

  onMemberJoined(listener: Listener<MemberInfo>): () => void {
    this.joinedListeners.add(listener);
    return () => this.joinedListeners.delete(listener);
  }

  onMemberLeft(listener: Listener<string>): () => void {
    this.leftListeners.add(listener);
    return () => this.leftListeners.delete(listener);
  }

  onMemberUpdate(
    listener: (
      id: string,
      patch: { muted?: boolean; height?: number; handRaised?: boolean },
    ) => void,
  ): () => void {
    this.memberUpdateListeners.add(listener);
    return () => this.memberUpdateListeners.delete(listener);
  }

  onPose(listener: (id: string, pose: PoseData) => void): () => void {
    this.poseListeners.add(listener);
    return () => this.poseListeners.delete(listener);
  }

  onError(listener: (code: string, message: string) => void): () => void {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  onSignal(listener: (from: string, data: unknown) => void): () => void {
    this.signalListeners.add(listener);
    return () => this.signalListeners.delete(listener);
  }
}
